import * as THREE from 'three';
import { HALO_LIVE_BREATHE, nestDepthDim, nodeEmissiveIntensity } from '../neuralMaterials';
import { stepSelectLerp, stepToward } from './selectLerp';

export { stepSelectLerp } from './selectLerp';

const SELECT_WHITE = new THREE.Color(0xffffff);

/** Stable 0..1 from an id — each pulsar keeps its own spin. */
function pulsarSeed(id) {
  const s = String(id ?? '');
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return (h >>> 0) / 4294967295;
}

/** Lighthouse flash: brief peak, short afterglow, faint between beats. */
function pulsarFlash(pulseT, id) {
  const seed = pulsarSeed(id);
  const period = 1.65 + seed * 1.15;
  const u = ((pulseT / period) + seed) % 1;
  const x = u > 0.5 ? u - 1 : u;
  const flash = Math.exp(-(x * x) * 95);
  const tail = Math.exp(-(x * x) * 16) * 0.28;
  return Math.min(1, flash + tail);
}

export function stepNodePresentation({
  meshByTaskId,
  labelByTaskId,
  placeLabelNearMesh,
  selectedTaskIdLocal,
  hoverPointerId,
  distScale,
  layoutDist,
  pulseT,
  reducedMotion,
  delta,
    igniting,
    NEURAL_TRANS_POS_LERP,
    NEURAL_LAYOUT_JUMP_LERP = NEURAL_TRANS_POS_LERP,
    NEURAL_TRANS_FADE_LERP,
}) {
  const meshesToCull = [];
  let anyMoving = false;
  const movedIds = new Set();

  meshByTaskId.forEach((mesh, tid) => {
    const ud = mesh.userData;
    const tgt = ud.targetPos;
    if (tgt) {
      if (!ud.homePos) ud.homePos = tgt.clone();
      if (ud.homePos.distanceToSquared(tgt) < 1e-6) {
        ud.homePos.copy(tgt);
        ud.layoutJump = false;
      } else {
        const k = ud.layoutJump ? NEURAL_LAYOUT_JUMP_LERP : NEURAL_TRANS_POS_LERP;
        ud.homePos.lerp(tgt, k);
        if (ud.homePos.distanceToSquared(tgt) < 1e-4) {
          ud.homePos.copy(tgt);
          ud.layoutJump = false;
        }
        anyMoving = true;
        movedIds.add(tid);
      }
    }

    let targetOp = ud.exiting ? 0 : ud.baseOpacity;
    const fadeDelta = (targetOp - ud.visT) * NEURAL_TRANS_FADE_LERP;
    ud.visT = THREE.MathUtils.clamp(ud.visT + fadeDelta, 0, 1);
    const fading = Math.abs(fadeDelta) > 0.001 || ud.exiting;

    const hovered = !ud.exiting && hoverPointerId === ud.taskId;
    const wasHovered = !!ud.hoverLerp;
    ud.hoverLerp = hovered ? 1 : 0;

    const meta = ud.nodeMeta;
    const isSel = ud.taskId === selectedTaskIdLocal;
    ud.selectLerp = stepSelectLerp(ud.selectLerp, isSel && !ud.exiting, delta, reducedMotion);
    const sel = ud.selectLerp;
    const pulsing = meta?.statusCategory === 'in_progress' && !ud.exiting && !reducedMotion;
    const live = fading || anyMoving || isSel || sel > 0.01 || pulsing || igniting;

    if (live) {
      let op = ud.visT;
      if (!ud.exiting && sel > 0.01) {
        const peak = meta?.statusCategory === 'not_started' ? 0.88 : ud.baseOpacity;
        op = peak * THREE.MathUtils.clamp(ud.visT / Math.max(ud.baseOpacity, 0.001), 0, 1);
      }
      mesh.material.opacity = THREE.MathUtils.clamp(sel > 0.01 ? Math.max(op, 0.92) : op, 0, 1);
      mesh.material.color.copy(ud.baseColor);
      if (sel > 0.001) mesh.material.color.lerp(SELECT_WHITE, 0.12 * sel);
      if (mesh.material.emissive) {
        mesh.material.emissive.copy(ud.baseColor);
        const dim = isSel || hovered ? 1 : nestDepthDim(meta);
        mesh.material.emissiveIntensity = nodeEmissiveIntensity(meta) * dim;
      }
      mesh.visible = ud.visT > 0.02;
    } else if ((hovered || wasHovered) && mesh.material.emissive) {
      mesh.material.emissive.copy(ud.baseColor);
      mesh.material.emissiveIntensity = nodeEmissiveIntensity(meta) * (hovered ? 1 : nestDepthDim(meta));
    }

    const vf = THREE.MathUtils.clamp(ud.visT / Math.max(ud.baseOpacity, 0.001), 0, 1);
    const visualS = distScale * (meta?.size ?? 1) * (1 + sel * 0.04) * vf;
    const layoutS = (layoutDist ?? distScale) * (meta?.size ?? 1) * vf;
    ud.diskScale = Math.max(visualS, 0.001);
    if (ud.layoutScale == null || Math.abs(ud.layoutScale - layoutS) > 0.05) {
      ud.layoutScale = Math.max(layoutS, 0.001);
    }
    const pulseMul = pulsing ? 1 + Math.sin(pulseT * 2) * 0.08 : 1;
    const nextScale = visualS * pulseMul;
    if (Math.abs(mesh.scale.x - nextScale) > 0.0008) {
      mesh.scale.setScalar(nextScale);
    }
    const labelRec = labelByTaskId.get(tid);
    if (labelRec?.label?.parent) placeLabelNearMesh(mesh, labelRec.label);

    ud.lastDistScale = distScale;

    if (ud.exiting && ud.visT < 0.008) {
      meshesToCull.push({ tid, mesh });
    }
  });

  return { anyMoving, movedIds, meshesToCull };
}

export function cullExitingMeshes({
  meshesToCull,
  meshByTaskId,
  labelByTaskId,
  removeHalo,
  detachLabelForTask,
  disposeMissionMesh,
}) {
  meshesToCull.forEach(({ tid, mesh }) => {
    removeHalo(tid);
    const lr = labelByTaskId.get(tid);
    if (lr) detachLabelForTask(tid, lr);
    disposeMissionMesh(mesh);
    meshByTaskId.delete(tid);
  });
}

export function stepHalos({
  haloByTaskId,
  selectedTaskIdLocal,
  pulseT,
  reducedMotion,
  igniting,
  anyMoving,
  delta,
  removeHalo,
}) {
  const gone = [];
  haloByTaskId.forEach((rec) => {
    const { halo, mesh: hm, pulse } = rec;
    if (anyMoving || pulse || igniting || hm.userData.nodeMeta?.isAvailable || hm.userData.taskId === selectedTaskIdLocal || (rec.glowLive ?? 0) > 0.01) {
      halo.position.copy(hm.position);
      halo.position.z -= 0.05;
    }
    const ud = hm.userData;
    const vis = THREE.MathUtils.clamp(ud.visT / Math.max(ud.baseOpacity, 0.001), 0, 1);
    const isSel = ud.taskId === selectedTaskIdLocal;
    ud.selectLerp = stepSelectLerp(ud.selectLerp, isSel && !ud.exiting, delta, reducedMotion);
    const sel = ud.selectLerp;
    const available = !!ud.nodeMeta?.isAvailable && !ud.exiting;
    const working = !!pulse && !ud.exiting;
    const live = available || working;
    rec.glowLive = stepToward(rec.glowLive, live ? 1 : 0, delta, reducedMotion, 0.07);
    rec.glowWork = stepToward(rec.glowWork, working && !reducedMotion ? 1 : 0, delta, reducedMotion, 0.08);
    rec.glowInner = stepToward(rec.glowInner, available ? 1 : 0, delta, reducedMotion, 0.08);
    const liveT = rec.glowLive;
    if (!hm.visible || vis < 0.02) {
      halo.visible = false;
      halo.material.opacity = 0;
      if (rec.keep === false && liveT < 0.01) gone.push(ud.taskId);
      return;
    }
    if (rec.keep === false && liveT < 0.01) {
      halo.visible = false;
      halo.material.opacity = 0;
      gone.push(ud.taskId);
      return;
    }
    halo.visible = liveT > 0.01;
    const quiet = ud.nodeMeta?.isLocked || ud.nodeMeta?.isExcluded;
    const dim = isSel ? 1 : nestDepthDim(ud.nodeMeta);
    const flash = available && !reducedMotion && !quiet ? pulsarFlash(pulseT, ud.taskId) : 0;
    const layout = ud.layoutScale > 0 ? ud.layoutScale : Math.max(hm.scale.x, 0.001);
    const nodeR = 0.155 * layout;
    const breathe = THREE.MathUtils.lerp(2.2, HALO_LIVE_BREATHE + flash * 0.35, liveT);
    halo.scale.setScalar(nodeR * breathe * (1 + sel * 0.08) * vis);
    halo.material.color.copy(ud.baseColor);
    if (sel > 0.001) halo.material.color.lerp(SELECT_WHITE, 0.08 * sel);
    if (halo.material.uniforms?.uPulse) halo.material.uniforms.uPulse.value = flash * liveT;
    if (halo.material.uniforms?.uSharp) halo.material.uniforms.uSharp.value = 0.28;
    if (halo.material.uniforms?.uTime) halo.material.uniforms.uTime.value = reducedMotion ? 0 : pulseT;
    if (halo.material.uniforms?.uElectrons) {
      halo.material.uniforms.uElectrons.value = rec.glowWork;
    }
    if (halo.material.uniforms?.uInner) {
      halo.material.uniforms.uInner.value = rec.glowInner;
    }
    const workOp = 0.30 + sel * 0.06 + (reducedMotion ? 0 : Math.sin(pulseT * 2) * 0.05);
    const rest = 0.13;
    const peak = 0.22;
    const selected = 0.30;
    const readyOp = quiet ? 0.05 : rest + (peak - rest) * flash;
    const body = THREE.MathUtils.lerp(readyOp, workOp, rec.glowWork);
    const lit = body + (selected - body) * sel;
    halo.material.opacity = lit * liveT * vis * dim;
  });
  if (removeHalo) gone.forEach((id) => removeHalo(id));
}
